import { gql, useMutation } from '@apollo/client';
import defaultFarmFields from '../../constants/defaultFarmFields';
import { AllVisibleTagsQuery, CurrentUserQuery } from '../../queries';
import { useQueryWithMsg } from '../../utils/hooks';
import FarmForm from './FarmForm';
import { UpdateFarmMutation } from '../../mutations';
import { useSetBreadcrumbEffect } from '../../contexts/BreadcrumbContext';
import {
  FarmBasicInfoForCropSelection,
  FarmCropTemplatesQuery,
  FarmTeamCropTemplatesQuery,
} from '../CropSelector';

const FarmQuery = gql`
  query GetFarm($farmId: ID!) {
    farm(farmId: $farmId) {
      id
      hasValidSubscription
      updatedAt
      owner {
        id
        email
      }
      team {
        id
        name
        allowedActions {
          id
          canEdit
        }
      }
      ${defaultFarmFields.join(' ')}
    }
  }
`;

const EditFarm = ({ farmId, showBreadcrumbs, ...props }) => {
  const { msg, data: queryData } = useQueryWithMsg(FarmQuery, {
    variables: { farmId },
    skip: !farmId,
  });

  useSetBreadcrumbEffect(
    showBreadcrumbs ? [{ display: msg || queryData?.farm.name }] : null,
    [msg, showBreadcrumbs, queryData],
  );

  const [updateFarmMutation] = useMutation(UpdateFarmMutation, {
    refetchQueries: [
      { query: CurrentUserQuery },
      { query: AllVisibleTagsQuery },
      // Refetch the queries used by CropSelector to update the cache
      { query: FarmBasicInfoForCropSelection, variables: { farmId } },
      { query: FarmTeamCropTemplatesQuery, variables: { farmId } },
      { query: FarmCropTemplatesQuery, variables: { farmId } },
    ],
    onCompleted: ({ updateFarm }) => {
      if (updateFarm.errors) {
        console.error(updateFarm.errors);
      }
    },
  });

  const { coordinates } = queryData?.farm.mapCenter.geometry || {};
  const mapCenter = coordinates
    ? { lat: coordinates[1], lng: coordinates[0] }
    : { lat: undefined, lng: undefined };

  const farmData = { ...(queryData?.farm ?? {}) };

  // Make the growing zone 'None' if it's not set
  if (farmData.growingZone == null) farmData.growingZone = 'None';

  const initialValues = {
    ...farmData,
    mapCenter,
  };

  return (
    msg || (
      <FarmForm
        saveFarmMutation={updateFarmMutation}
        showLabels
        initialValues={initialValues}
        submitButtonText="Update Farm"
        successMessage="Farm updated successfully"
        {...props}
      />
    )
  );
};

export default EditFarm;
